import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateBulkFromPhotos } from '../hooks/useObjets';
import { useToastContext } from '../contexts/ToastContext';
import { compressImageFile } from '../utils/compressImage';
import {
  compressFileToBlob,
  fetchStorageConfig,
  uploadPhotoBlob,
} from '../utils/photoStorage';

export type BulkPhotoLieuTarget = {
  type: 'zone' | 'box' | 'piece';
  id: string;
  label: string;
  subtitle?: string;
};

interface BulkPhotoLieuModalProps {
  isOpen: boolean;
  onClose: () => void;
  target: BulkPhotoLieuTarget | null;
}

type PendingPhoto = {
  id: string;
  file: File;
  previewUrl: string;
  status: 'pending' | 'processing' | 'ready' | 'error';
  payload?: string;
  error?: string;
};

const MAX_PHOTOS = 20;

const BulkPhotoLieuModal: React.FC<BulkPhotoLieuModalProps> = ({ isOpen, onClose, target }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToastContext();
  const bulkMutation = useCreateBulkFromPhotos();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [directUpload, setDirectUpload] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPhotos([]);
      setIsSubmitting(false);
      fetchStorageConfig().then((cfg) => setDirectUpload(cfg.directUpload));
    } else {
      setPhotos((prev) => {
        prev.forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
        return [];
      });
    }
  }, [isOpen, target?.id]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (incoming.length === 0) {
      showWarning('Photos requises', 'Sélectionnez des images JPEG ou PNG.');
      return;
    }

    setPhotos((prev) => {
      const remaining = MAX_PHOTOS - prev.length;
      if (remaining <= 0) {
        showWarning('Limite atteinte', `Maximum ${MAX_PHOTOS} photos par session.`);
        return prev;
      }
      const slice = incoming.slice(0, remaining);
      if (incoming.length > remaining) {
        showWarning('Limite atteinte', `Seules ${remaining} photo(s) supplémentaire(s) ont été ajoutées.`);
      }
      return [
        ...prev,
        ...slice.map((file) => ({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
          status: 'pending' as const,
        })),
      ];
    });
  }, [showWarning]);

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const row = prev.find((p) => p.id === id);
      if (row) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const preparePhotoPayload = async (row: PendingPhoto): Promise<string> => {
    if (directUpload) {
      const blob = await compressFileToBlob(row.file);
      const uploaded = await uploadPhotoBlob(blob, { filename: row.file.name });
      return uploaded.reference;
    }
    return compressImageFile(row.file);
  };

  const handleSubmit = async () => {
    if (!target) return;
    if (photos.length === 0) {
      showWarning('Photos requises', 'Ajoutez au moins une photo.');
      return;
    }

    setIsSubmitting(true);
    setPhotos((prev) => prev.map((p) => ({ ...p, status: 'processing', error: undefined })));

    try {
      const payloads: string[] = [];

      for (const row of photos) {
        try {
          const payload = await preparePhotoPayload(row);
          payloads.push(payload);
          setPhotos((prev) =>
            prev.map((p) => (p.id === row.id ? { ...p, status: 'ready', payload } : p)),
          );
        } catch (err: any) {
          const message = err?.message || 'Échec de préparation';
          setPhotos((prev) =>
            prev.map((p) => (p.id === row.id ? { ...p, status: 'error', error: message } : p)),
          );
          throw new Error(message);
        }
      }

      const locationPayload =
        target.type === 'zone'
          ? { zoneId: target.id }
          : target.type === 'box'
            ? { boxId: target.id }
            : { pieceId: target.id };

      const result = await bulkMutation.mutateAsync({
        photos: payloads,
        ...locationPayload,
      });

      showSuccess(
        'Objets créés',
        `${result.count} objet(s) brouillon ajouté(s) dans « ${target.label} ».`,
      );
      onClose();
      navigate('/inventaire?filter=ficheStatut%3Abrouillon');
    } catch (err: any) {
      showError('Erreur', err.response?.data?.message || err.message || 'Création impossible');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !target) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block w-full max-w-3xl transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900">📷 Ajouter des objets par photos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Lieu : <span className="font-medium text-gray-800">{target.label}</span>
                  {target.subtitle ? ` — ${target.subtitle}` : ''}
                </p>
                <p className="mt-1 text-xs text-amber-700">
                  1 photo = 1 objet « à compléter » (brouillon). Complétez nom et catégorie ensuite.
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting || photos.length >= MAX_PHOTOS}
                className="min-h-[44px] flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:flex-none"
              >
                📸 Prendre / choisir des photos
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting || photos.length >= MAX_PHOTOS}
                className="min-h-[44px] rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 hidden sm:inline-flex"
              >
                Parcourir…
              </button>
            </div>

            <div
              className="mb-4 rounded-md border-2 border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 hidden sm:block"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
              }}
            >
              Glissez-déposez vos photos ici (max {MAX_PHOTOS})
            </div>

            {photos.length > 0 && (
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative overflow-hidden rounded-lg border border-gray-200">
                    <img src={photo.previewUrl} alt="" className="h-28 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      disabled={isSubmitting}
                      className="absolute right-1 top-1 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white"
                    >
                      ✕
                    </button>
                    {photo.status === 'processing' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs">
                        Traitement…
                      </div>
                    )}
                    {photo.status === 'error' && (
                      <div className="absolute inset-x-0 bottom-0 bg-red-600 px-1 py-0.5 text-[10px] text-white">
                        {photo.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-500">
              {photos.length}/{MAX_PHOTOS} photo(s) — compression automatique avant envoi
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 bg-gray-50 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 sm:w-auto"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || photos.length === 0}
              className="min-h-[44px] w-full rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 sm:w-auto"
            >
              {isSubmitting ? 'Création…' : `Créer ${photos.length} objet(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPhotoLieuModal;
